<template>
  <div class="d-flex h-100 position-relative">
    <!-- Sidebar -->
    <v-card
      v-show="!mobile || drawerOpen"
      :class="mobile ? 'sidebar sidebar-mobile' : 'sidebar'"
      :elevation="mobile ? 4 : 0"
      rounded="0"
    >
      <v-list density="compact" nav>
        <v-progress-circular v-if="loading" class="ma-4 d-flex mx-auto" color="primary" indeterminate />
        <template v-else>
          <v-list-item
            v-for="b in list"
            :key="b.id"
            :active="selectedGroup === b.id"
            :title="b.name"
            @click="selectGroup(b.id)"
          />
          <v-list-item v-if="list.length === 0" disabled title="Aucun groupe" />
        </template>
        <v-divider class="my-1" />
        <v-list-item
          prepend-icon="mdi-plus"
          title="Créer un groupe"
          @click="selectGroup('add-group')"
        />
      </v-list>
    </v-card>

    <!-- Overlay mobile -->
    <div
      v-if="mobile && drawerOpen"
      class="overlay-backdrop"
      @click="drawerOpen = false"
    />

    <!-- Contenu principal -->
    <v-sheet class="flex-fill h-100 overflow-auto">
      <v-btn
        v-if="mobile && selectedGroup"
        class="ma-2"
        icon="mdi-arrow-left"
        size="small"
        variant="text"
        @click="drawerOpen = true"
      />
      <template v-if="selectedGroup">
        <the-family-group-component :key="selectedGroup" :family-group-id="selectedGroup" @deleted="onGroupDeleted" @update:family-group="onFamilyGroupUpdated" />
      </template>
    </v-sheet>

    <!-- Dialog ajout de groupe -->
    <v-dialog v-model="showWizard" max-width="500" persistent>
      <v-card>
        <v-card-title>Créer un groupe familial</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newGroupName"
            autofocus
            :disabled="creatingGroup"
            label="Nom du groupe"
            :rules="[v => !!v || 'Le nom est requis']"
            @keyup.enter="tryCreateGroup"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn :disabled="creatingGroup" variant="text" @click="closeWizard">Annuler</v-btn>
          <v-btn color="primary" :disabled="!newGroupName || creatingGroup" :loading="creatingGroup" @click="tryCreateGroup">Créer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts" setup>
  import type { FamilyGroup } from '@/model/family-group.ts'
  import { storeToRefs } from 'pinia'
  import { onMounted, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useDisplay } from 'vuetify'
  import { familyGroupRepository } from '@/api/repository/familygroup.repository.ts'
  import TheFamilyGroupComponent from '@/components/TheFamilyGroupComponent.vue'
  import { useAppStore } from '@/stores/app.ts'
  import { ToastType, useToastStore } from '@/stores/toast.store.ts'
  import { notifyApiError } from '@/utils/error.utils.ts'

  const route = useRoute()
  const router = useRouter()
  const { mobile } = useDisplay()
  const { familygroupsDrawerOpen: drawerOpen } = storeToRefs(useAppStore())

  const selectedGroup = ref<string | null>(null)
  const list = ref<FamilyGroup[]>([])
  const loading = ref(true)
  const showWizard = ref(false)

  // Pour la création de groupe
  const newGroupName = ref('')
  const creatingGroup = ref(false)

  async function tryCreateGroup () {
    if (!newGroupName.value.trim()) return
    creatingGroup.value = true
    try {
      await familyGroupRepository.createFamilyGroup({ name: newGroupName.value.trim() })
      useToastStore().notify('Groupe créé', ToastType.Success)
      showWizard.value = false
      newGroupName.value = ''
      await load()
    } catch (error) {
      notifyApiError('Erreur lors de la création du groupe', error)
    } finally {
      creatingGroup.value = false
    }
  }

  function closeWizard () {
    showWizard.value = false
    newGroupName.value = ''
  }

  async function selectGroup (id: string) {
    if (id === 'add-group') {
      showWizard.value = true
      return
    }
    if (mobile.value) {
      drawerOpen.value = false
    }
    await router.replace({ name: 'familygroup', params: { familyGroupId: id }, query: {} })
    selectedGroup.value = id
  }

  function onFamilyGroupUpdated (updated: FamilyGroup) {
    const index = list.value.findIndex(b => b.id === updated.id)
    if (index !== -1) {
      list.value[index] = updated
    }
  }

  function syncSelectionFromRoute () {
    switch (route.name) {
      case 'familygroup': {
        selectedGroup.value = route.params.familyGroupId as string
        if (mobile.value) drawerOpen.value = false
        break
      }
      default: {
        selectedGroup.value = null
        drawerOpen.value = true
        break
      }
    }
  }

  watch(() => route.fullPath, syncSelectionFromRoute)

  async function load () {
    try {
      list.value = await familyGroupRepository.list()
      syncSelectionFromRoute()
    } catch (error) {
      notifyApiError('Impossible de charger la liste des groupes familiaux', error)
    } finally {
      loading.value = false
    }
  }

  async function onGroupDeleted () {
    selectedGroup.value = null
    if (!mobile.value) {
      drawerOpen.value = true
    }
    await router.replace({ name: 'familygroups' })
    await load()
  }

  onMounted(() => {
    loading.value = true
    load()
  })
</script>

<style scoped>
.sidebar {
  width: 250px;
  min-width: 250px;
  height: 100%;
  border-right: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.sidebar-mobile {
  position: absolute;
  top: 0;
  left: 0;
  width: 80%;
  max-width: 300px;
  z-index: 11;
}

.overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10;
}
</style>
